import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Shell from "@/components/Shell";
import api from "@/lib/api";
import {
    ThumbsUp,
    ThumbsDown,
    Minus,
    ArrowLeft,
    Check,
    X,
    Trash2,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";

const VERDICT = {
    up: {
        Icon: ThumbsUp,
        color: "#3B824E",
        bg: "#EAF0E9",
        label: "Thumbs Up",
        text: "This suits you.",
    },
    sideways: {
        Icon: Minus,
        color: "#C47A55",
        bg: "#F6ECE3",
        label: "Sideways",
        text: "Mixed — proceed with care.",
    },
    down: {
        Icon: ThumbsDown,
        color: "#AD4A42",
        bg: "#F5E4E2",
        label: "Thumbs Down",
        text: "Better to skip.",
    },
};

export default function Result() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [data, setData] = useState(location.state?.analysis || null);
    const [loading, setLoading] = useState(!data);

    useEffect(() => {
        if (data) return;
        (async () => {
            try {
                const r = await api.get(`/history/${id}`);
                setData(r.data);
            } catch {
                toast.error("Could not load analysis");
                navigate("/history");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, data, navigate]);

    const del = async () => {
        if (!window.confirm("Delete this analysis?")) return;
        try {
            await api.delete(`/history/${id}`);
            toast.success("Deleted");
            navigate("/history");
        } catch {
            toast.error("Delete failed");
        }
    };

    if (loading || !data) {
        return (
            <Shell>
                <div className="flex items-center gap-2 text-[#4A5A4E]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
            </Shell>
        );
    }

    const v = VERDICT[data.verdict] || VERDICT.sideways;
    const { Icon } = v;
    const scorePct = (data.score / 10) * 100;

    return (
        <Shell>
            <div className="nl-fade-up">
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-1 text-sm text-[#4A5A4E] hover:text-[#1A3626]"
                        data-testid="result-back"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button
                        onClick={del}
                        className="flex items-center gap-1 text-sm text-[#AD4A42] hover:opacity-80"
                        data-testid="result-delete"
                    >
                        <Trash2 className="w-4 h-4" /> Delete
                    </button>
                </div>

                <p className="font-eyebrow mb-2">Analysis</p>
                <h1
                    className="font-serif-editorial text-3xl sm:text-4xl tracking-tight leading-tight"
                    style={{ color: "#1A3626" }}
                    data-testid="result-product-name"
                >
                    {data.product_name}
                </h1>
                <p className="text-xs text-[#8BA092] mt-1">
                    {new Date(data.created_at).toLocaleString()}
                </p>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Score card */}
                    <div
                        className="nl-card flex flex-col items-center justify-center py-8 relative"
                        data-testid="result-score-card"
                    >
                        <p className="font-eyebrow mb-4">Score</p>
                        <div className="relative w-36 h-36">
                            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="44"
                                    stroke="#EAF0E9"
                                    strokeWidth="8"
                                    fill="none"
                                />
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="44"
                                    stroke={v.color}
                                    strokeWidth="8"
                                    fill="none"
                                    strokeDasharray={`${(scorePct / 100) * 276.46} 276.46`}
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span
                                    className="font-serif-editorial text-[56px] leading-none"
                                    style={{ color: "#1A3626" }}
                                    data-testid="result-score-value"
                                >
                                    {data.score}
                                </span>
                                <span className="text-[11px] text-[#8BA092] mt-1">
                                    out of 10
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Verdict card */}
                    <div
                        className="nl-card flex flex-col items-center justify-center py-8 gap-3"
                        style={{ background: v.bg }}
                        data-testid="result-verdict-card"
                    >
                        <p className="font-eyebrow" style={{ color: v.color }}>
                            Verdict
                        </p>
                        <div
                            className="w-24 h-24 rounded-full flex items-center justify-center"
                            style={{ background: "rgba(255,255,255,0.6)" }}
                        >
                            <Icon className="w-12 h-12" style={{ color: v.color }} />
                        </div>
                        <p
                            className="font-serif-editorial text-2xl"
                            style={{ color: "#1A3626" }}
                        >
                            {v.label}
                        </p>
                        <p className="text-sm text-[#4A5A4E] text-center max-w-[260px]">
                            {v.text}
                        </p>
                    </div>
                </div>

                {/* Summary */}
                <div className="mt-4 nl-card">
                    <p className="font-eyebrow mb-2">Summary</p>
                    <p className="text-[15px] leading-relaxed text-[#1A3626]" data-testid="result-summary">
                        {data.summary}
                    </p>
                </div>

                {/* Pros / Cons */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="nl-card">
                        <p className="font-eyebrow mb-3" style={{ color: "#3B824E" }}>
                            Why it works
                        </p>
                        <ul className="space-y-2.5">
                            {data.pros?.length === 0 && (
                                <li className="text-sm text-[#8BA092]">—</li>
                            )}
                            {data.pros?.map((p, i) => (
                                <li key={i} className="flex items-start gap-2.5">
                                    <span
                                        className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                        style={{ background: "#EAF0E9" }}
                                    >
                                        <Check className="w-3 h-3" style={{ color: "#3B824E" }} />
                                    </span>
                                    <span className="text-[14px] text-[#1A3626]">{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="nl-card">
                        <p className="font-eyebrow mb-3" style={{ color: "#AD4A42" }}>
                            Watch-outs
                        </p>
                        <ul className="space-y-2.5">
                            {data.cons?.length === 0 && (
                                <li className="text-sm text-[#8BA092]">—</li>
                            )}
                            {data.cons?.map((c, i) => (
                                <li key={i} className="flex items-start gap-2.5">
                                    <span
                                        className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                        style={{ background: "#F5E4E2" }}
                                    >
                                        <X className="w-3 h-3" style={{ color: "#AD4A42" }} />
                                    </span>
                                    <span className="text-[14px] text-[#1A3626]">{c}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {data.image_base64 && (
                    <div className="mt-4 nl-card">
                        <p className="font-eyebrow mb-3">Your photo</p>
                        <img
                            src={
                                data.image_base64.startsWith("data:")
                                    ? data.image_base64
                                    : `data:image/jpeg;base64,${data.image_base64}`
                            }
                            alt={data.product_name}
                            className="w-full rounded-2xl aspect-[4/3] object-cover"
                        />
                    </div>
                )}
            </div>
        </Shell>
    );
}
